import { Box, Button, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import { useSelectedProperty } from '../../shared/useSelectedProperty';
import tokens from '../../shared/theme/tokens';
import PageHeader from '../../components/PageHeader';
import Panel from '../../components/Panel';
import ConfirmDialog from '../../components/ConfirmDialog';
import BannerUploadDialog, { BANNER_SPECS } from '../properties/BannerUploadDialog';

export default function Banners() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get('/v1/admin/properties'),
  });
  const isSuperAdmin = queryClient.getQueryData(['user'])?.data?.isSuperAdmin;

  const [selectedProperty, setSelectedProperty] = useSelectedProperty();

  // Banner state
  // bannerError/homeBannerError now only cover the narrow case of a recorded
  // banner whose object is genuinely missing from the bucket. Existence itself
  // comes from the DB timestamp (see bannerUrl below).
  const [bannerError, setBannerError] = useState(false);
  const [homeBannerError, setHomeBannerError] = useState(false);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  // Which banner the dialog is uploading: 'landing' ({abbr}.png) or 'home' ({abbr}-home.png).
  const [bannerVariant, setBannerVariant] = useState('landing');
  // Delete confirmation: 'landing' | 'home' | null
  const [deleteConfirmVariant, setDeleteConfirmVariant] = useState(null);

  // Click-through link inputs, one per banner variant. Seeded from the property
  // record and edited locally until saved.
  const [landingLink, setLandingLink] = useState('');
  const [homeLink, setHomeLink] = useState('');
  // Home banner audience gate. Seeded from the property record; saved on change.
  const [homeAudience, setHomeAudience] = useState('all');

  useEffect(() => {
    if (data && data.data && data.data.length && !selectedProperty) {
      setSelectedProperty(data.data[0].id);
    }
  }, [data, selectedProperty, setSelectedProperty]);

  // Reset banner error state when selected property changes
  useEffect(() => {
    setBannerError(false);
    setHomeBannerError(false);
  }, [selectedProperty]);

  const selectedPropertyData = data?.data.find(d => d.id === selectedProperty);
  const abbreviation = selectedPropertyData?.abbreviation;

  // Seed the link inputs whenever the selected property (or refetched data) changes.
  useEffect(() => {
    setLandingLink(selectedPropertyData?.bannerLink ?? '');
    setHomeLink(selectedPropertyData?.homeBannerLink ?? '');
    setHomeAudience(selectedPropertyData?.homeBannerAudience ?? 'all');
  }, [selectedPropertyData?.id, selectedPropertyData?.bannerLink, selectedPropertyData?.homeBannerLink, selectedPropertyData?.homeBannerAudience]);

  const bannerLinkMutation = useMutation({
    mutationFn: ({ variant, link }) => api.put(`/v1/admin/properties/${selectedProperty}/banner-link?variant=${variant}`, { link }),
  });

  const bannerAudienceMutation = useMutation({
    mutationFn: ({ audience }) => api.put(`/v1/admin/properties/${selectedProperty}/banner-audience`, { audience }),
  });

  // Hardcoded promo banners. These are defined in the API (helpers/bannerCampaigns.ts)
  // and render on every property automatically, so there is nothing to upload or
  // enable here — only a per-property Remove.
  const { data: campaignsData } = useQuery({
    queryKey: ['banner-campaigns'],
    queryFn: () => api.get('/v1/admin/banner-campaigns'),
    enabled: !!isSuperAdmin,
  });

  const campaignToggleMutation = useMutation({
    mutationFn: ({ key, enabled }) => api.put(`/v1/admin/properties/${selectedProperty}/banner-campaigns/${key}`, { enabled }),
  });

  const suppressedCampaigns = selectedPropertyData?.suppressedBannerCampaigns ?? [];

  // Which campaign's artwork dialog is open, and a client-side cache-buster so
  // the preview refreshes after an upload. Campaign artwork lives at a fixed
  // CDN key with no version param, so without this the admin would keep seeing
  // the stale (or missing) image they just replaced.
  const [artworkCampaignKey, setArtworkCampaignKey] = useState(null);
  const [artworkReloadAt, setArtworkReloadAt] = useState(null);

  const handleToggleCampaign = async (key, enabled) => {
    try {
      await campaignToggleMutation.mutateAsync({ key, enabled });
      toast.success(enabled ? 'Promo banner restored for this property' : 'Promo banner removed for this property');
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update promo banner');
    }
  };

  const handleSaveAudience = async audience => {
    const previous = selectedPropertyData?.homeBannerAudience ?? 'all';
    setHomeAudience(audience); // optimistic
    try {
      await bannerAudienceMutation.mutateAsync({ audience });
      toast.success('Home banner audience saved');
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    } catch (err) {
      setHomeAudience(previous); // roll back on failure
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save banner audience');
    }
  };

  const savedLandingLink = selectedPropertyData?.bannerLink ?? '';
  const savedHomeLink = selectedPropertyData?.homeBannerLink ?? '';

  const handleSaveLink = async variant => {
    const link = (variant === 'home' ? homeLink : landingLink).trim();
    try {
      await bannerLinkMutation.mutateAsync({ variant, link });
      toast.success('Banner link saved');
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save banner link');
    }
  };

  // The DB timestamp is the source of truth for "a banner exists" — NOT whether
  // the image happens to load. The CloudFront distribution in front of this
  // bucket excludes query strings from its cache key, so after a delete it keeps
  // serving the removed object for hours (verified in prod: a deleted banner
  // returned 200 from cache with age 13676s while the column was already NULL).
  // Probing the image therefore reports a deleted banner as still present, which
  // made Delete look broken. A null timestamp now means no banner, full stop.
  const bannerTs = selectedPropertyData?.bannerUpdatedAt ? new Date(selectedPropertyData.bannerUpdatedAt).getTime() : null;
  const homeBannerTs = selectedPropertyData?.homeBannerUpdatedAt ? new Date(selectedPropertyData.homeBannerUpdatedAt).getTime() : null;

  const bannerUrl = abbreviation && bannerTs ? `https://d2b0f54uipcwhi.cloudfront.net/banners/${abbreviation}.png?t=${bannerTs}` : null;
  const homeBannerUrl = abbreviation && homeBannerTs ? `https://d2b0f54uipcwhi.cloudfront.net/banners/${abbreviation}-home.png?t=${homeBannerTs}` : null;

  const bannerDeleteMutation = useMutation({
    mutationFn: variant => api.delete(`/v1/admin/properties/${selectedProperty}/banner${variant === 'home' ? '?variant=home' : ''}`),
  });

  const openBannerDialog = variant => {
    setBannerVariant(variant);
    setBannerDialogOpen(true);
  };

  const handleBannerDelete = async () => {
    if (!deleteConfirmVariant) return;
    try {
      await bannerDeleteMutation.mutateAsync(deleteConfirmVariant);
      toast.success('Banner deleted');
      // Reset error state so the image re-attempts load (and gets a 404, showing "No banner")
      if (deleteConfirmVariant === 'home') {
        setHomeBannerError(false);
      } else {
        setBannerError(false);
      }
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
    } catch (err) {
      toast.error(err?.message || 'Failed to delete banner');
    } finally {
      setDeleteConfirmVariant(null);
      bannerDeleteMutation.reset();
    }
  };

  return (
    <Box>
      <PageHeader
        title="Banners"
        action={
          <Select
            size="small"
            value={selectedProperty}
            onChange={e => setSelectedProperty(e.target.value)}
            sx={{ minWidth: 260, backgroundColor: 'background.paper' }}
          >
            {data?.data.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        }
      />

      {selectedProperty && isSuperAdmin && (
        <Panel title="Property banners">
          <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
            Property Banner
          </Typography>
          <Typography sx={{ mb: 1, fontSize: '13px', color: 'text.secondary' }}>{BANNER_SPECS.landing.hint}</Typography>
          <Stack flexDirection="row" alignItems="center" gap={2}>
            {bannerUrl && !bannerError ? (
              <Box
                component="img"
                src={bannerUrl}
                alt="Property banner"
                onError={() => setBannerError(true)}
                sx={{
                  height: 80,
                  maxWidth: 320,
                  objectFit: 'contain',
                  borderRadius: `${tokens.radius.sm}px`,
                  border: `1px solid ${tokens.colors.border}`,
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 80,
                  width: 320,
                  borderRadius: `${tokens.radius.sm}px`,
                  border: `1px dashed ${tokens.colors.borderStrong}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: tokens.colors.hover,
                }}
              >
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>No banner uploaded</Typography>
              </Box>
            )}
            <Button variant="outlined" size="small" onClick={() => openBannerDialog('landing')}>
              {bannerUrl && !bannerError ? 'Replace Banner' : 'Upload Banner'}
            </Button>
            {bannerUrl && !bannerError && (
              <Button variant="outlined" size="small" color="error" onClick={() => setDeleteConfirmVariant('landing')}>
                Delete Banner
              </Button>
            )}
          </Stack>

          <BannerLinkField
            value={landingLink}
            onChange={setLandingLink}
            onSave={() => handleSaveLink('landing')}
            dirty={landingLink.trim() !== savedLandingLink}
            saving={bannerLinkMutation.isPending && bannerLinkMutation.variables?.variant === 'landing'}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.25 }}>
            Home Banner
          </Typography>
          <Typography sx={{ mb: 1, fontSize: '13px', color: 'text.secondary' }}>{BANNER_SPECS.home.hint}</Typography>
          <Stack flexDirection="row" alignItems="center" gap={2}>
            {homeBannerUrl && !homeBannerError ? (
              <Box
                component="img"
                src={homeBannerUrl}
                alt="Home banner"
                onError={() => setHomeBannerError(true)}
                sx={{
                  height: 80,
                  maxWidth: 320,
                  objectFit: 'contain',
                  borderRadius: `${tokens.radius.sm}px`,
                  border: `1px solid ${tokens.colors.border}`,
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 80,
                  width: 320,
                  borderRadius: `${tokens.radius.sm}px`,
                  border: `1px dashed ${tokens.colors.borderStrong}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: tokens.colors.hover,
                }}
              >
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>No home banner uploaded</Typography>
              </Box>
            )}
            <Button variant="outlined" size="small" onClick={() => openBannerDialog('home')}>
              {homeBannerUrl && !homeBannerError ? 'Replace Home Banner' : 'Upload Home Banner'}
            </Button>
            {homeBannerUrl && !homeBannerError && (
              <Button variant="outlined" size="small" color="error" onClick={() => setDeleteConfirmVariant('home')}>
                Delete Home Banner
              </Button>
            )}
          </Stack>

          <BannerLinkField
            value={homeLink}
            onChange={setHomeLink}
            onSave={() => handleSaveLink('home')}
            dirty={homeLink.trim() !== savedHomeLink}
            saving={bannerLinkMutation.isPending && bannerLinkMutation.variables?.variant === 'home'}
          />

          <BannerAudienceField value={homeAudience} onChange={handleSaveAudience} saving={bannerAudienceMutation.isPending} />
        </Panel>
      )}

      {selectedProperty && isSuperAdmin && campaignsData?.data?.length > 0 && (
        <Panel title="Promo banners" sx={{ mt: 2 }}>
          <Typography sx={{ mb: 2, fontSize: '13px', color: 'text.secondary' }}>
            These run on every property automatically — there is nothing to upload. Removing one hides it for <strong>{selectedPropertyData?.name}</strong>{' '}
            only, and you can restore it at any time.
          </Typography>
          <Stack gap={2}>
            {campaignsData.data.map(campaign => (
              <CampaignRow
                key={campaign.key}
                campaign={campaign}
                removed={suppressedCampaigns.includes(campaign.key)}
                saving={campaignToggleMutation.isPending && campaignToggleMutation.variables?.key === campaign.key}
                onToggle={enabled => handleToggleCampaign(campaign.key, enabled)}
                onUploadArtwork={() => setArtworkCampaignKey(campaign.key)}
                reloadAt={artworkReloadAt}
              />
            ))}
          </Stack>
        </Panel>
      )}

      <BannerUploadDialog
        open={!!artworkCampaignKey}
        variant="home"
        campaignKey={artworkCampaignKey}
        propertyId={selectedProperty}
        onClose={() => setArtworkCampaignKey(null)}
        onUploaded={() => {
          setArtworkCampaignKey(null);
          setArtworkReloadAt(Date.now());
        }}
      />

      <BannerUploadDialog
        open={bannerDialogOpen}
        variant={bannerVariant}
        propertyId={selectedProperty}
        onClose={() => setBannerDialogOpen(false)}
        onUploaded={() => setBannerDialogOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteConfirmVariant}
        onClose={() => setDeleteConfirmVariant(null)}
        title={`Delete ${deleteConfirmVariant === 'home' ? 'Home Banner' : 'Property Banner'}?`}
        body="This will remove the banner immediately for all residents. This cannot be undone."
        cancelLabel="Cancel"
        confirmLabel="Delete"
        destructive
        loading={bannerDeleteMutation.isPending}
        onConfirm={handleBannerDelete}
      />
    </Box>
  );
}

/**
 * Optional click-through link for a banner. When set, the resident app renders
 * the banner as a link. An in-app path ("/rent/reporting/home") navigates the
 * resident inside the portal in the same tab; a full external URL
 * ("https://…") opens in a new tab. Leave blank for a plain, non-clickable
 * banner. Save is enabled only when the value differs from what's stored. The
 * server validates and normalizes the value.
 *
 * The field is a plain text input (not type="url") so an in-app path like
 * "/rent/reporting/home" passes browser validation.
 */
function BannerLinkField({ value, onChange, onSave, dirty, saving }) {
  return (
    <Stack sx={{ mt: 1.5, maxWidth: 560 }} gap={0.5}>
      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
        Click-through link (optional) — an in-app path like <code>/rent/reporting/home</code> opens inside the portal in the same tab; a full{' '}
        <code>https://</code> URL opens in a new tab
      </Typography>
      <Stack flexDirection="row" alignItems="center" gap={1.5}>
        <TextField
          size="small"
          fullWidth
          type="text"
          placeholder="/rent/reporting/home  or  https://example.com/promo"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
        <Button variant="contained" size="small" disabled={!dirty || saving} onClick={onSave} sx={{ whiteSpace: 'nowrap' }}>
          {saving ? 'Saving…' : 'Save Link'}
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * Audience gate for the home banner. "All residents" (default) shows the banner
 * to everyone. "Only residents not enrolled in CredBuild" hides it from
 * residents who have already signed up — use it for CredBuild enrollment-promo
 * banners so they only reach residents who can still act on them. Saves on
 * change (no separate Save button).
 */
function BannerAudienceField({ value, onChange, saving }) {
  return (
    <Stack sx={{ mt: 2, maxWidth: 560 }} gap={0.5}>
      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Audience — who sees this home banner</Typography>
      <Select size="small" value={value} disabled={saving} onChange={e => onChange(e.target.value)} sx={{ backgroundColor: 'background.paper' }}>
        <MenuItem value="all">All residents</MenuItem>
        <MenuItem value="credbuild_unenrolled">Only residents not enrolled in CredBuild</MenuItem>
      </Select>
    </Stack>
  );
}

/**
 * One hardcoded promo banner, with its artwork preview and a per-property
 * Remove / Restore toggle. Nothing here uploads or edits the banner itself —
 * the image, link, and audience are set in code and apply to every property, so
 * the only property-level decision is whether to show it at all.
 */
function CampaignRow({ campaign, removed, saving, onToggle, onUploadArtwork, reloadAt }) {
  const [imageError, setImageError] = useState(false);

  // Retry the image whenever an upload finishes, bypassing the browser cache.
  useEffect(() => {
    setImageError(false);
  }, [reloadAt]);

  const previewUrl = reloadAt ? `${campaign.imageUrl}?r=${reloadAt}` : campaign.imageUrl;

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={2}
      sx={{
        p: 1.5,
        borderRadius: `${tokens.radius.sm}px`,
        border: `1px solid ${tokens.colors.border}`,
        opacity: removed ? 0.55 : 1,
      }}
    >
      {!imageError ? (
        <Box
          component="img"
          src={previewUrl}
          alt={campaign.label}
          onError={() => setImageError(true)}
          sx={{
            height: 64,
            maxWidth: 256,
            objectFit: 'contain',
            borderRadius: `${tokens.radius.sm}px`,
            border: `1px solid ${tokens.colors.border}`,
          }}
        />
      ) : (
        <Box
          sx={{
            height: 64,
            width: 256,
            borderRadius: `${tokens.radius.sm}px`,
            border: `1px dashed ${tokens.colors.borderStrong}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tokens.colors.hover,
          }}
        >
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Artwork not uploaded yet</Typography>
        </Box>
      )}

      <Stack gap={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2">{campaign.label}</Typography>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          {campaign.audience === 'credbuild_unenrolled' ? 'Shown only to residents not enrolled in CredBuild' : 'Shown to all residents'}
        </Typography>
        {campaign.link && <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Links to {campaign.link}</Typography>}
        {removed && <Typography sx={{ fontSize: '13px', color: tokens.colors.warning.fg }}>Removed for this property</Typography>}
      </Stack>

      <Stack flexDirection="row" alignItems="center" gap={1}>
        <Button variant="outlined" size="small" onClick={onUploadArtwork} sx={{ whiteSpace: 'nowrap' }}>
          {imageError ? 'Upload Artwork' : 'Replace Artwork'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color={removed ? 'primary' : 'error'}
          disabled={saving}
          onClick={() => onToggle(removed)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {saving ? 'Saving…' : removed ? 'Restore' : 'Remove'}
        </Button>
      </Stack>
    </Stack>
  );
}
